import { Component } from '@theme/component';
import { eventBus, NewsletterSubscribedEvent } from '@theme/events';
import { subscribeToNewsletter } from '@theme/newsletter--api';

/**
 * Newsletter Form — Klaviyo list subscription with optional name field.
 *
 * Progressive enhancement over a native GET form pointing at Klaviyo's hosted
 * subscribe page. With JS, the submit is intercepted (after native HTML5
 * validation passes) and sent to Klaviyo's Client API so the user stays on
 * the page. Without `data-company-id`/`data-list-id` the native form submits
 * untouched.
 *
 * State feedback via `data-state` on the host element:
 * '' → 'loading' → 'success' | 'error'.
 *
 * @attr {string} data-company-id - Klaviyo public API key (site ID)
 * @attr {string} data-list-id - Klaviyo list ID
 * @attr {string} data-source - Klaviyo custom_source (signup surface)
 * @attr {string} [data-template] - Current page's template (e.g. 'page.email-signup'), combined with data-source into the internal_source Klaviyo profile property
 * @attr {string} data-success-message - Localized success message
 * @attr {string} data-error-message - Localized error message
 * @attr {string} data-invalid-message - Localized invalid-email message
 *
 * Validation: the form is switched to `novalidate` on connect (no native
 * browser tooltips) and the email is checked with the old theme's pattern.
 * `[data-valid]` on the host reflects a valid email (drives the submit
 * button's active state); an invalid submit shows data-invalid-message.
 *
 * @typedef {object} Refs
 * @property {HTMLFormElement} form
 * @property {HTMLInputElement} email
 * @property {HTMLInputElement} [name]
 * @property {HTMLElement} message
 *
 * @extends {Component<Refs>}
 */
class NewsletterForm extends Component {
	requiredRefs = ['form', 'email', 'message'];

	/** Same pattern as the old theme's newsletter validation. */
	static EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

	/** @type {boolean} */
	#isSubmitting = false;

	connectedCallback() {
		super.connectedCallback();
		// JS takes over validation; without JS the native `required` still applies.
		this.refs.form.noValidate = true;
		this.#syncValidity();
	}

	/**
	 * Keeps [data-valid] in sync while typing, and clears the invalid-email
	 * message as soon as the address becomes valid.
	 */
	handleEmailInput() {
		const isValid = this.#syncValidity();
		if (isValid && this.dataset.state === 'invalid') {
			delete this.dataset.state;
			this.refs.message.hidden = true;
		}
	}

	/**
	 * Handles the form submit. Invalid emails never reach Klaviyo — the
	 * invalid-email message is shown instead.
	 *
	 * @param {SubmitEvent} event
	 */
	async handleSubmit(event) {
		const { companyId, listId, source, template } = this.dataset;

		if (!this.#syncValidity()) {
			event.preventDefault();
			this.#showMessage('invalid');
			return;
		}

		if (!companyId || !listId) return;

		event.preventDefault();

		if (this.#isSubmitting) return;
		this.#isSubmitting = true;
		this.dataset.state = 'loading';
		this.refs.message.hidden = true;

		const fullName = this.refs.name?.value.trim() ?? '';
		const [firstName, ...rest] = fullName.split(/\s+/);

		try {
			await subscribeToNewsletter({
				companyId,
				listId,
				email: this.refs.email.value.trim(),
				firstName,
				lastName: rest.join(' '),
				source: source || 'Newsletter',
				template,
			});

			this.#showMessage('success');
			this.refs.form.reset();
			this.#syncValidity();
			eventBus.emit(new NewsletterSubscribedEvent({ data: { source: source || 'Newsletter' } }));
		} catch (error) {
			console.error('[newsletter-form]', error);
			this.#showMessage('error');
		} finally {
			this.#isSubmitting = false;
		}
	}

	/**
	 * Updates [data-valid] from the current email value.
	 * @returns {boolean} Whether the email is valid
	 */
	#syncValidity() {
		const isValid = NewsletterForm.EMAIL_PATTERN.test(this.refs.email.value.trim());
		this.toggleAttribute('data-valid', isValid);
		return isValid;
	}

	/**
	 * @param {'success' | 'error' | 'invalid'} state
	 */
	#showMessage(state) {
		const messages = {
			success: this.dataset.successMessage,
			error: this.dataset.errorMessage,
			invalid: this.dataset.invalidMessage,
		};
		this.dataset.state = state;
		this.refs.message.textContent = messages[state] ?? '';
		this.refs.message.hidden = false;
	}
}

if (!customElements.get('newsletter-form')) {
	customElements.define('newsletter-form', NewsletterForm);
}
